import { useState, useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/SEO";
import { getArticleBySlug, BLOG_ARTICLES } from "@/blog/articles";
import { ArticleHeader, ArticleBlock, BlogCard } from "@/components/BlogParts";
import { logger } from "@/lib/logger";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const renderBlock = (block, i) => {
  const stableKey = typeof block.content === "string"
    ? `${block.type}-${block.content.slice(0, 40)}`
    : `${block.type}-${i}`;
  return <ArticleBlock key={stableKey} block={block} />;
};
const renderRelated = (a) => <BlogCard key={a.slug} article={a} />;

export default function BlogArticlePage() {
  const { slug } = useParams();
  const staticArticle = getArticleBySlug(slug);

  // If not in static articles, try fetching from DB (AI-generated)
  const [dbArticle, setDbArticle] = useState(null);
  const [loadingDb, setLoadingDb] = useState(!staticArticle);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (staticArticle) return;
    let cancelled = false;
    fetch(`${API_URL}/api/blog/articles/${slug}`)
      .then(async (r) => {
        if (cancelled) return;
        if (r.ok) {
          const item = await r.json();
          setDbArticle(item);
        } else {
          setNotFound(true);
        }
      })
      .catch((err) => {
        logger.error("Article fetch failed:", err);
        if (!cancelled) setNotFound(true);
      })
      .finally(() => { if (!cancelled) setLoadingDb(false); });
    return () => { cancelled = true; };
  }, [slug, staticArticle]);

  const article = staticArticle || dbArticle;

  if (loadingDb) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="blog-article-loading">
        <Loader2 className="h-8 w-8 animate-spin text-red-600" />
      </div>
    );
  }
  if (notFound || !article) {
    return <Navigate to="/blog" replace />;
  }

  const articleTitle = article.title;
  const articleDescription = article.description;
  const articleKeywords = article.keywords;
  const articleDate = article.date;
  const articleAuthor = article.author;
  const articleImage = article.image;
  const articleSections = article.sections;
  const articleSlug = article.slug;

  const related = BLOG_ARTICLES.filter((a) => a.slug !== articleSlug).slice(0, 3);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": articleTitle,
    "description": articleDescription,
    "image": articleImage,
    "datePublished": articleDate,
    "dateModified": articleDate,
    "author": {
      "@type": "Organization",
      "name": articleAuthor,
      "url": "https://www.mediastore79.com"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Mediastore",
      "logo": {
        "@type": "ImageObject",
        "url": "https://customer-assets.emergentagent.com/job_2010016e-c6ae-4f83-80db-30e5f9c887c1/artifacts/7vmwauk8_LOGO%20MEDIASTORE.JPG"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://www.mediastore79.com/blog/${articleSlug}`
    }
  };

  return (
    <div className="bg-white min-h-screen" data-testid="blog-article-page">
      <SEO
        title={`${articleTitle} | Blog Mediastore Niort`}
        description={articleDescription}
        keywords={articleKeywords}
        path={`/blog/${articleSlug}`}
        image={articleImage}
        type="article"
        schema={articleSchema}
      />

      <article className="container-custom max-w-3xl section-padding">
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-8 text-sm"
          data-testid="back-to-blog"
        >
          <ArrowLeft className="h-4 w-4" /> Tous les articles
        </Link>

        <ArticleHeader article={article} />

        <div className="prose prose-lg max-w-none">
          {articleSections.map(renderBlock)}
        </div>
      </article>

      {related.length > 0 && (
        <section className="bg-slate-50 section-padding" data-testid="blog-related">
          <div className="container-custom max-w-6xl">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-8 text-center">
              À lire aussi
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {related.map(renderRelated)}
            </div>
            <div className="text-center mt-10">
              <Link to="/blog">
                <Button variant="outline" data-testid="related-see-all-btn">
                  Voir tous les articles
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
